import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  untracked,
  ViewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { BottomStickyComponent } from '@app/_layout/bottom-sticky/bottom-sticky.component';
import { TopBarComponent } from '@app/_layout/top-bar/top-bar.component';
import { selectTicketLimitPerEmail } from '@app/app.reducer';
import { BasketComponent } from '@app/basket/basket.component';
import { BrowserCheckComponent } from '@app/browser-check/browser-check.component';
import { ColorizerComponent } from '@app/colorizer/colorizer.component';
import { FooterComponent } from '@app/layout/footer/footer.component';
import { LsButtonComponent, LsModalComponent, LsSidebarComponent, LsToastSimpleComponent } from '@axess/ng-limestone';
import { AccessibilitySidebar } from '@components/accessibility-sidebar/accessibility-sidebar';
import { VerifyPhoneModal } from '@components/verify-phone-modal/verify-phone-modal';
import { FormStepValidityService } from '@core/data-access/services/form/form-step-validity.service';
import { FormTicketLimitValidity } from '@core/data-access/services/form/form-ticket-limit-validity';
import { FormValidityService } from '@core/data-access/services/form/form-validity.service';
import { ScrollToService } from '@core/data-access/services/scroll-to.service';
import { selectIsHighContrastEnabled } from '@core/data-access/store/accessibility/accessibility.selectors';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { HideTranslationPipe } from '@shared/pipes/hide-translation/hide-translation.pipe';
import { StatusBarService } from '@shared/status-bar/status-bar.service';
import { BigLoaderComponent } from '@ui/components/big-loader/big-loader.component';
import { TicketLimitPerEmailAddressModalComponent } from '@ui/components/ticket-limit-per-email-address-modal/ticket-limit-per-email-address-component';
import { AddAttributes } from '@ui/directives/add-attributes';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    TopBarComponent,
    RouterOutlet,
    FooterComponent,
    BrowserCheckComponent,
    BottomStickyComponent,
    BigLoaderComponent,
    ColorizerComponent,
    TranslateModule,
    AddAttributes,
    HideTranslationPipe,
    BasketComponent,
    TicketLimitPerEmailAddressModalComponent,
    AccessibilitySidebar,
    LsModalComponent,
    LsButtonComponent,
    LsToastSimpleComponent,
    LsSidebarComponent,
    VerifyPhoneModal
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss'
})
export class MainLayoutComponent implements OnInit, AfterViewInit {
  readonly #injector = inject(Injector);
  readonly #store = inject(Store);
  readonly #statusBarService = inject(StatusBarService);
  readonly #formValidityService = inject(FormValidityService);
  readonly #formTicketLimitValidity = inject(FormTicketLimitValidity);
  readonly #formStepValidity = inject(FormStepValidityService);
  readonly #scrollToService = inject(ScrollToService);

  readonly isTranslationLoaded = input.required<boolean>();
  readonly isSelfRegistration = input.required<boolean>();
  readonly isLoggedInAsAdmin = input.required<boolean>();
  readonly isReloadModalWindowOpen = input.required<boolean>();
  readonly backgroundImage = input.required<string>();

  @ViewChild(LsToastSimpleComponent) readonly toast!: LsToastSimpleComponent;
  @ViewChild(TopBarComponent, { read: ElementRef }) readonly topBarComponentRef!: ElementRef;

  readonly rightClickEvent = output<MouseEvent>();
  readonly closeModalWindowEvent = output<MouseEvent>();

  readonly isBasketOpen = signal<boolean>(false);
  readonly isMobileMenuOpen = signal<boolean>(false);

  readonly #toastStatusSignal = toSignal(this.#statusBarService.statusStack$, {
    initialValue: { message: '', type: null }
  });

  protected readonly isHighContrastEnabled = this.#store.selectSignal(selectIsHighContrastEnabled);
  protected readonly ticketLimitPerEmail = this.#store.selectSignal(selectTicketLimitPerEmail);

  protected readonly isInvalidBuyerInformationStepModalOpen =
    this.#formStepValidity.getIsInvalidBuyerInformationStepModalOpen();
  protected readonly isDuplicateEmailAddressWarningMessageModalOpen =
    this.#formValidityService.getIsDuplicateEmailAddressWarningMessageModalOpen();
  protected readonly ticketLimitPerEmailAddressWarningMessageModalState =
    this.#formTicketLimitValidity.getTicketLimitPerEmailAddressWarningMessageModalState();

  ngOnInit(): void {
    this.initToastStatusEffect();
  }

  ngAfterViewInit(): void {
    this.#scrollToService.setTopBarElement(this.topBarComponentRef.nativeElement);
  }

  protected onRightClick($event: MouseEvent) {
    this.rightClickEvent.emit($event);
  }

  protected onCloseModalWindow($event: MouseEvent) {
    this.closeModalWindowEvent.emit($event);
  }

  protected onCloseDuplicateEmailAddressWarningMessageModal($event: MouseEvent) {
    $event.stopPropagation();

    this.#formValidityService.setIsDuplicateEmailAddressWarningMessageModalOpen(false);
  }

  protected onCloseTicketLimitPerEmailAddressWarningMessageModal() {
    this.#formTicketLimitValidity.setTicketLimitPerEmailAddressWarningMessageModalState(false, true);
  }

  protected onRedirectToBuyerInformationStep($event: MouseEvent) {
    $event.stopPropagation();

    this.#formStepValidity.redirectToBuyerInformationStep();
  }

  protected onCloseBasket($event: boolean) {
    this.isBasketOpen.set($event);
  }

  protected onIsBasketOpen($event: boolean) {
    this.isBasketOpen.set($event);
  }

  protected onCloseMobileMenu($event: boolean) {
    this.isMobileMenuOpen.set($event);
  }

  protected onIsMobileMenuOpen($event: boolean) {
    this.isMobileMenuOpen.set($event);
  }

  private initToastStatusEffect() {
    effect(
      () => {
        const { message, type } = this.#toastStatusSignal();

        untracked(() => {
          this.setToast(message, type);
        });
      },
      { injector: this.#injector }
    );
  }

  private setToast(message: string, type: string) {
    switch (type) {
      case 'success':
        this.toast.success(message);
        break;
      case 'error':
        this.toast.error(message);
        break;
    }
  }
}
